import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react"
import { apiClient } from "@/services/api.client"
import { AuthContext } from "@/providers/AuthProvider"
import type { Thread } from "@/types"

interface ThreadContextValue {
  threads: Thread[]
  activeThread: Thread | null
  isLoading: boolean
  /** True once the first thread fetch has completed (regardless of result). */
  threadsInitialized: boolean
  activeServiceId: string
  createThread: (title: string, serviceId?: string) => Promise<Thread>
  deleteThread: (threadId: string) => Promise<void>
  setActiveThread: (thread: Thread | null) => void
  setActiveServiceId: (id: string) => void
  refreshThreads: () => Promise<void>
}

export const ThreadContext = createContext<ThreadContextValue | null>(null)

/** sessionStorage key — scoped per-tab, cleared on tab close. */
const ACTIVE_THREAD_KEY = "nitiassist:activeThreadId"

export function ThreadProvider({ children }: { children: ReactNode }) {
  const auth = useContext(AuthContext)
  const [threads, setThreads] = useState<Thread[]>([])
  const [activeThread, setActiveThreadState] = useState<Thread | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [threadsInitialized, setThreadsInitialized] = useState(false)
  const [activeServiceId, setActiveServiceId] = useState<string>("oio")

  // Wrap setActiveThread so we persist the selected ID to sessionStorage.
  // This lets refreshThreads() restore the selection after a page refresh.
  const setActiveThread = useCallback((thread: Thread | null) => {
    setActiveThreadState(thread)
    setThreads((prev) => {
      if (!thread) {
        return prev
      }
      const next = prev.map((existing) => (existing.id === thread.id ? thread : existing))
      return next.some((existing) => existing.id === thread.id) ? next : [thread, ...prev]
    })
    if (thread) {
      sessionStorage.setItem(ACTIVE_THREAD_KEY, thread.id)
    } else {
      sessionStorage.removeItem(ACTIVE_THREAD_KEY)
    }
  }, [])

  const refreshThreads = useCallback(async () => {
    if (!auth?.session) return
    setIsLoading(true)
    try {
      const data = await apiClient.get<Thread[]>("/api/v1/threads/")
      setThreads(data)

      // Restore the previously selected thread after a page refresh.
      // We read from sessionStorage (not state) because on cold mount
      // activeThread is always null — sessionStorage is the only durable
      // source within the same tab session.
      const savedId = sessionStorage.getItem(ACTIVE_THREAD_KEY)
      if (savedId) {
        const match = data.find((t) => t.id === savedId)
        if (match) {
          // Use setActiveThreadState (not setActiveThread) to avoid
          // re-writing sessionStorage for a value that was already there.
          setActiveThreadState(match)
        } else {
          // Thread no longer exists (deleted on another device/tab).
          sessionStorage.removeItem(ACTIVE_THREAD_KEY)
        }
      }
    } finally {
      setIsLoading(false)
      // Mark initialized after the first fetch regardless of outcome so that
      // consumers that gate on this flag don't wait forever on network errors.
      setThreadsInitialized(true)
    }
  }, [auth?.session])

  useEffect(() => {
    if (auth?.session) {
      refreshThreads()
    } else {
      setThreads([])
      setActiveThreadState(null)
      sessionStorage.removeItem(ACTIVE_THREAD_KEY)
      setThreadsInitialized(false)
    }
  }, [auth?.session, refreshThreads])

  const createThread = useCallback(
    async (title: string, serviceId?: string): Promise<Thread> => {
      const thread = await apiClient.post<Thread>("/api/v1/threads/", {
        title,
        metadata: {
          user_id: auth?.session?.user.id,
          service_id: serviceId ?? activeServiceId,
        },
      })
      setThreads((prev) => [thread, ...prev])
      setActiveThread(thread)
      return thread
    },
    [auth?.session?.user.id, activeServiceId, setActiveThread]
  )

  const deleteThread = useCallback(async (threadId: string) => {
    await apiClient.delete(`/api/v1/threads/${threadId}`)
    setThreads((prev) => prev.filter((t) => t.id !== threadId))
    // Clear activeThread if the deleted thread was selected.
    setActiveThreadState((prev) => {
      if (prev?.id === threadId) {
        sessionStorage.removeItem(ACTIVE_THREAD_KEY)
        return null
      }
      return prev
    })
  }, [])

  const contextValue = useMemo(
    () => ({
      threads,
      activeThread,
      isLoading,
      threadsInitialized,
      activeServiceId,
      createThread,
      deleteThread,
      setActiveThread,
      setActiveServiceId,
      refreshThreads,
    }),
    [
      threads,
      activeThread,
      isLoading,
      threadsInitialized,
      activeServiceId,
      createThread,
      deleteThread,
      setActiveThread,
      setActiveServiceId,
      refreshThreads,
    ],
  )

  return (
    <ThreadContext.Provider value={contextValue}>{children}</ThreadContext.Provider>
  )
}

export function useThreadContext() {
  const ctx = useContext(ThreadContext)
  if (!ctx) throw new Error("useThreadContext must be used inside ThreadProvider")
  return ctx
}
